
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import {
  Box,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";

import { InputDate } from "../../controls";
import * as actions from "../../api/actions/pos";
import { controller, endpoints } from "../../api/actions/api";
import { AppContext } from "../../App";
import { safeOnComplete } from "../../utils/safeOnComplete";
import { formatCurrency } from "../../hooks/Currency";
import ReportPrintButton from "./ReportPrintButton";
import moment from "moment";

const StoreReports = (props) => {
  const { t } = useTranslation();
  const { _post, _getById, _store } = props;
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);

  const [searchVal, setSearchVal] = useState(new Date());

  const [sells, setSells] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [voidSells, setVoidSells] = useState([]);
  const [cancelCarts, setCancelCarts] = useState([]);
  const [inventories, setInventories] = useState([]);

  const [grossProfit, setGrossProfit] = useState(0);
  const [totalExps, setTotalExps] = useState(0);
  const [totalSell, setTotalSell] = useState(0);
  const [netProfit, setNetProfit] = useState(0);
  const [totalItem, setTotalItem] = useState(0);
  const [voidAmount, setVoidAmount] = useState(0);
  const [refund, setRefund] = useState(0);
  const [openingInvVal, setOpeningInvVal] = useState(0);
  const [newInvVal, setNewInvVal] = useState(0);
  const [soldInvVal, setSoldInvVal] = useState(0);
  const [totalPurch, setTotalPurch] = useState(0);
  const [currentInvVal, setCurrentInvVal] = useState(0);

  const handleSearch = (date) => {
    setSearchVal(date);
  };

  // Load current inventory value
  useEffect(() => {
    if (!_store?.id) {
      setCurrentInvVal(0);
      return;
    }

    const onComplete = (res, err) => {
      safeOnComplete(
        (response) => {
          if (Array.isArray(response.result)) {
            const total = response.result.reduce(
              (sum, item) =>
                sum +
                Number(item.product?.sellPrice || 0) *
                  Number(item.inInventory || 0),
              0
            );

            setCurrentInvVal(total);
          } else {
            setCurrentInvVal(0);
          }
        },
        (error) => {
          console.error(
            "Failed to load inventory:",
            error
          );
          setCurrentInvVal(0);
        }
      )(res, err);
    };

    _getById(
      endpoints.Inventory + controller.GetProdsByStore,
      _store.id,
      null,
      onComplete
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  // Load monthly store report
  useEffect(() => {
    if (!searchVal || !_store?.id) {
      setSells([]);
      setExpenses([]);
      setVoidSells([]);
      setCancelCarts([]);
      setInventories([]);
      return;
    }

    setLoader(true);

    const onComplete = (res, err) => {
      safeOnComplete(
        (response) => {
          if (response.status === 200) {
            const result = response.result || {};

            setSells(
              Array.isArray(result.sells)
                ? result.sells
                : []
            );

            setExpenses(
              Array.isArray(result.expense)
                ? result.expense
                : []
            );

            setVoidSells(
              Array.isArray(result.voidSells)
                ? result.voidSells
                : []
            );

            setCancelCarts(
              Array.isArray(result.cancelCarts)
                ? result.cancelCarts
                : []
            );

            setInventories(
              Array.isArray(result.inventories)
                ? result.inventories
                : []
            );
          } else {
            setSells([]);
            setExpenses([]);
            setVoidSells([]);
            setCancelCarts([]);
            setInventories([]);
          }
        },
        (error) => {
          console.error(
            "Failed to load store report:",
            error
          );

          setSells([]);
          setExpenses([]);
          setVoidSells([]);
          setCancelCarts([]);
          setInventories([]);
        }
      )(res, err);

      setLoader(false);
    };

    _post(
      endpoints.Store + controller.StoreReport,
      {
        storeId: _store.id,
        value: moment(searchVal).format("MM/yyyy"),
      },
      null,
      onComplete
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchVal, _store?.id]);

  // Calculate store report totals
  useEffect(() => {
    const _totalSells = sells.reduce(
      (sum, item) =>
        sum + Number(item.subtotal || 0),
      0
    );

    setTotalSell(_totalSells);

    const sellItems = sells.flatMap(
      (item) => item.sellItems || []
    );

    const _totalSoldInv = sellItems
      .filter((item) => item.itemType === 1)
      .reduce(
        (sum, item) =>
          sum +
          Number(item.product?.sellPrice || 0) *
            Number(item.qty || 0),
        0
      );

    setSoldInvVal(_totalSoldInv);

    const _totalPurchase = sellItems
      .filter((item) => item.itemType === 1)
      .reduce(
        (sum, item) =>
          sum +
          Number(item.product?.purchPrice || 0) *
            Number(item.qty || 0),
        0
      );

    setTotalPurch(_totalPurchase);

    const _grossProfit =
      _totalSells - _totalPurchase;

    setGrossProfit(_grossProfit);

    const _totalExps = expenses.reduce(
      (sum, item) =>
        sum + Number(item.amount || 0),
      0
    );

    setTotalExps(_totalExps);

    setNetProfit(
      _grossProfit - _totalExps
    );

    const _totalItem = sellItems.reduce(
      (sum, item) =>
        sum + Number(item.qty || 0),
      0
    );

    setTotalItem(_totalItem);

    const _refunds = voidSells
      .filter((item) => item.cancelType === 2)
      .reduce(
        (sum, item) =>
          sum + Number(item.subtotal || 0),
        0
      );

    setRefund(_refunds);

    const _voidAmount = voidSells
      .filter((item) => item.cancelType === 1)
      .reduce(
        (sum, item) =>
          sum + Number(item.subtotal || 0),
        0
      );

    setVoidAmount(_voidAmount);

    const _openingInvVal = inventories.reduce(
      (sum, item) =>
        sum +
        Number(item.product?.sellPrice || 0) *
          Number(item.inInventory || 0),
      0
    );

    setOpeningInvVal(_openingInvVal);

    const _newInvVal = inventories.reduce(
      (sum, item) => {
        const addedInv = (
          item.invUsers || []
        ).reduce(
          (total, row) =>
            total + Number(row.addedInv || 0),
          0
        );

        return (
          sum +
          Number(item.product?.sellPrice || 0) *
            addedInv
        );
      },
      0
    );

    setNewInvVal(_newInvVal);
  }, [sells, expenses, voidSells, inventories]);

  // Reusable report layout for screen and print
  const ReportContent = () => (
    <>
      {/* Report heading */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("storeReport")}
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{ textAlign: "center" }}
        >
          {_store?.storeName || ""}
        </Typography>

        <Typography
          variant="body2"
          sx={{ textAlign: "center" }}
        >
          {moment(searchVal).format("MMMM YYYY")}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {/* Gross Profit */}
        <Grid item xs={12} md={6}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              height: "100%",
              boxSizing: "border-box",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold" }}
            >
              {t("grossProfit")}
            </Typography>

            <Typography
              variant="body2"
              sx={{ mb: 1 }}
            >
              {t("grossProfitDescription")}
            </Typography>

            <Typography
              variant="h5"
              sx={{ fontWeight: "bold" }}
            >
              {formatCurrency(
                grossProfit,
                _store
              )}
            </Typography>
          </Paper>
        </Grid>

        {/* Net Profit */}
        <Grid item xs={12} md={6}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              height: "100%",
              boxSizing: "border-box",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold" }}
            >
              {t("netProfit")}
            </Typography>

            <Typography
              variant="body2"
              sx={{ mb: 1 }}
            >
              {t("netProfitDescription")}
            </Typography>

            <Typography
              variant="h5"
              sx={{ fontWeight: "bold" }}
            >
              {formatCurrency(
                netProfit,
                _store
              )}
            </Typography>
          </Paper>
        </Grid>

        {/* Inventory */}
        <Grid item xs={12} md={6}>
          <Paper
            variant="outlined"
            sx={{ p: 2 }}
          >
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                mb: 1,
              }}
            >
              {t("inventory")}
            </Typography>

            <Divider sx={{ mb: 1 }} />

            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>
                    {t("openingInventoryValue")}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(
                      openingInvVal,
                      _store
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("newInventoryAdded")}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(
                      newInvVal,
                      _store
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("inventorySold")}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(
                      soldInvVal,
                      _store
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("currentInventoryValue")}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(
                      currentInvVal,
                      _store
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Store Details */}
        <Grid item xs={12} md={6}>
          <Paper
            variant="outlined"
            sx={{ p: 2 }}
          >
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                mb: 1,
              }}
            >
              {t("storeDetails")}
            </Typography>

            <Divider sx={{ mb: 1 }} />

            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>
                    {t("totalItemSold")}
                  </TableCell>
                  <TableCell align="right">
                    {totalItem}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("totalVoidSale")}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(
                      voidAmount,
                      _store
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("totalRefund")}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(
                      refund,
                      _store
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("totalItemCancelledFromCart")}
                  </TableCell>
                  <TableCell align="right">
                    {cancelCarts.length}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Items */}
        <Grid item xs={12} md={6}>
          <Paper
            variant="outlined"
            sx={{ p: 2 }}
          >
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                mb: 1,
              }}
            >
              {t("items")}
            </Typography>

            <Divider sx={{ mb: 1 }} />

            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>
                    {t("totalSale")}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(
                      totalSell,
                      _store
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("totalPurchase")}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(
                      totalPurch,
                      _store
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("totalItemSold")}
                  </TableCell>
                  <TableCell align="right">
                    {totalItem}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Expenses */}
        <Grid item xs={12} md={6}>
          <Paper
            variant="outlined"
            sx={{ p: 2 }}
          >
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                mb: 1,
              }}
            >
              {t("expenses")}
            </Typography>

            <Divider sx={{ mb: 1 }} />

            <Table size="small">
              <TableBody>
                {expenses.map((exp, index) => (
                  <TableRow
                    key={exp.id ?? index}
                  >
                    <TableCell>
                      {exp.expenseCat?.name || ""}
                    </TableCell>

                    <TableCell align="right">
                      {formatCurrency(
                        exp.amount,
                        _store
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold" }}
                  >
                    {t("totalExpenses")}
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{ fontWeight: "bold" }}
                  >
                    {formatCurrency(
                      totalExps,
                      _store
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>
    </>
  );

  return (
    <>
      {/* Screen controls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("storeReport")}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("storeReport")}
        />
      </Box>

      {/* Monthly filter - screen only */}
      <Paper
        className="shadow-xs"
        sx={{ mb: 3 }}
      >
        <InputDate
          label={t("searchByMonth")}
          value={searchVal}
          views={["year", "month"]}
          openTo="month"
          variant="filled"
          onChange={(date) => {
            if (
              date &&
              !Number.isNaN(
                new Date(date).getTime()
              )
            ) {
              handleSearch(new Date(date));
            }
          }}
          fullWidth
        />
      </Paper>

      {/* Normal screen report */}
      <Box
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <ReportContent />
      </Box>

      {/* Separate printable report */}
      <Box
        ref={reportRef}
        className="store-report-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <ReportContent />
      </Box>
    </>
  );
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _post: actions.post,
  _getById: actions.getById,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(StoreReports);
